import { openCookieSettings } from '@/components/consent/CookieConsent';
import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Building2, ArrowRight, MessageCircle, Facebook, Linkedin, Send, CheckCircle, Rss } from 'lucide-react';
import { m, type Variants, type Easing } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { submitForm, submitErrorMessage } from '@/lib/formSubmit';
import HoneypotField from '@/components/forms/HoneypotField';
import logoColor from '@/assets/logo-mtb-btp-color.webp';
import logoGlobal from '@/assets/logo-mtb-global.png';
import { trackCtaClick, type CtaChannel } from '@/lib/ctaTracking';
import { PHONE_NUMBER_DISPLAY, PHONE_NUMBER_LINK, WHATSAPP_LINK, WHATSAPP_NUMBER_DISPLAY, FACEBOOK_LINK, LINKEDIN_LINK } from '@/constants/contact';
import { getProfileUrl, getProfileDownloadName } from '@/constants/companyProfile';

const easeOut: Easing = [0.0, 0.0, 0.2, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: easeOut },
  }),
};

const staggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const staggerItem: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
};

const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, delay: i * 0.1, ease: easeOut },
  }),
};

const Footer = () => {
  const { t, language } = useLanguage();
  const { pathname } = useLocation();
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const startedAt = useRef(Date.now());

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newsletterStatus === 'loading') return;
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    setNewsletterStatus('loading');
    setNewsletterError('');
    const result = await submitForm({
      formType: 'newsletter',
      locale: language,
      consent: true,
      honeypot,
      startedAt: startedAt.current,
      payload: { email },
    });

    if (result.ok || result.code === 'duplicate') {
      setNewsletterStatus('success');
      setEmail('');
    } else {
      setNewsletterStatus('error');
      setNewsletterError(submitErrorMessage(result.code, language !== 'en'));
    }
    startedAt.current = Date.now();
    setTimeout(() => setNewsletterStatus('idle'), 5000);
  };

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/methodology', label: t('nav.methodology') },
    { path: '/references', label: t('nav.references') },
    { path: '/news', label: t('nav.news') },
  ];

  const infoLinks = [
    { path: '/compliance', label: t('nav.compliance') },
    { path: '/tenders', label: t('nav.tenders') },
    { path: '/partners', label: language === 'fr' ? 'Partenaires & Clients' : 'Partners & Clients' },
    { path: '/careers', label: language === 'fr' ? 'Carrières' : 'Careers' },
    { path: '/faq', label: t('nav.faq') },
    { path: '/documents', label: language === 'fr' ? 'Documents & modèles' : 'Documents & templates' },
    { path: '/quote-request', label: t('nav.quote') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/install', label: language === 'fr' ? 'Installer l\'app' : 'Install app' },
    { path: '/legal-notice', label: t('footer.legal_notice') },
    { path: '/privacy-policy', label: t('footer.privacy_policy') },
  ];


  const socialLinks = [
    { href: FACEBOOK_LINK, icon: Facebook, label: 'Facebook' },
    { href: LINKEDIN_LINK, icon: Linkedin, label: 'LinkedIn' },
    { href: WHATSAPP_LINK, icon: MessageCircle, label: 'WhatsApp' },
    {
      href: `https://dpkqguapplsxaozkhfnc.supabase.co/functions/v1/news-rss?lang=${language}`,
      icon: Rss,
      label: language === 'fr' ? 'Flux RSS' : 'RSS feed',
    },
  ];

  const contactItems = [
    {
      type: 'div' as const,
      icon: MapPin,
      title: t('footer.contact.hq'),
      content: <>N° 62, Avenue Kikwit, Quartier Pinzi<br />Kalamu, Kinshasa – RDC</>,
    },
    {
      type: 'a' as const,
      href: 'mailto:contact@mtbbtp.com',
      icon: Mail,
      title: 'Email',
      content: 'contact@mtbbtp.com',
    },
    {
      type: 'a' as const,
      href: PHONE_NUMBER_LINK,
      icon: Phone,
      title: t('contact.phone'),
      content: PHONE_NUMBER_DISPLAY,
      cta: 'call' as CtaChannel,
    },
    {
      type: 'a' as const,
      href: WHATSAPP_LINK,
      target: '_blank',
      icon: MessageCircle,
      title: 'WhatsApp',
      content: WHATSAPP_NUMBER_DISPLAY,
      cta: 'whatsapp' as CtaChannel,
    },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary/90" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      {/* Newsletter CTA Band */}
      <m.div
        className="relative border-b border-primary-foreground/10"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container mx-auto px-4 py-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <m.div variants={fadeUp} custom={0} className="text-center lg:text-left max-w-md">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-accent mb-2">
                {t('footer.cta_title')}
              </h3>
              <p className="text-primary-foreground/70 text-sm md:text-base">
                {t('footer.newsletter_subtitle')}
              </p>
            </m.div>

            <m.div variants={fadeUp} custom={1} className="flex flex-col sm:flex-row items-center gap-4 w-full lg:w-auto">
              <form onSubmit={handleNewsletter} method="post" className="relative flex w-full sm:w-auto">
                <HoneypotField value={honeypot} onChange={setHoneypot} idPrefix="newsletter" />
                <div className="relative flex items-center w-full sm:w-80">
                  <label htmlFor="newsletter-email" className="sr-only">
                    {t('footer.newsletter_placeholder')}
                  </label>
                  <input
                    id="newsletter-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    maxLength={255}
                    aria-describedby="newsletter-consent"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer.newsletter_placeholder')}
                    className="w-full px-4 py-3 pr-12 rounded-l-xl bg-surface-glass/10 backdrop-blur-md border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/40 focus:outline-none focus:border-accent/60 focus:bg-surface-glass/15 transition-all text-sm"
                    required
                    disabled={newsletterStatus === 'loading'}
                  />
                  <button
                    type="submit"
                    disabled={newsletterStatus === 'loading'}
                    className="px-5 py-3 bg-accent hover:bg-accent/90 text-accent-foreground rounded-r-xl font-semibold transition-all flex items-center gap-2 disabled:opacity-50 text-sm whitespace-nowrap"
                  >
                    {newsletterStatus === 'success' ? <CheckCircle className="w-5 h-5" /> : <Send className="w-4 h-4" />}
                    {t('footer.newsletter_btn')}
                  </button>
                </div>
              </form>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-transparent border-2 border-accent text-accent px-6 py-2.5 rounded-xl font-semibold hover:bg-accent hover:text-accent-foreground transition-all group whitespace-nowrap text-sm"
              >
                {t('cta.contact')}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </m.div>
          </div>

          <p id="newsletter-consent" className="text-primary-foreground/60 text-xs mt-3 text-center lg:text-right">
            {language === 'en'
              ? 'By subscribing you agree to receive our news. You can unsubscribe at any time.'
              : 'En vous inscrivant, vous acceptez de recevoir nos actualités. Désinscription possible à tout moment.'}
          </p>
          <div aria-live="polite">
            {newsletterStatus === 'success' && (
              <p className="text-accent text-sm mt-3 text-center lg:text-right animate-fade-in">
                {t('footer.newsletter_success')}
              </p>
            )}
            {newsletterStatus === 'error' && (
              <p className="text-red-400 text-sm mt-3 text-center lg:text-right animate-fade-in">
                {newsletterError || t('footer.newsletter_error')}
              </p>
            )}
          </div>
        </div>
      </m.div>

      {/* Main Content */}
      <div className="relative container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-6">

          {/* Brand — 4 cols */}
          <m.div
            className="lg:col-span-4 space-y-5"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={0}
          >
            <m.div
              className="bg-surface-glass/10 backdrop-blur-md rounded-2xl p-4 inline-block border border-surface-glass/10 hover:border-accent/30 transition-colors"
              whileHover={{ scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <img src={logoColor} alt="MTB BTP" className="h-16 w-auto" width={256} height={171} loading="lazy" decoding="async" />
            </m.div>
            <p className="text-sm text-primary-foreground/70 leading-relaxed max-w-xs">
              {t('footer.company_desc')}
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3 pt-2">
              {socialLinks.map(({ href, icon: Icon, label }, i) => (
                <m.a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="group w-10 h-10 rounded-xl bg-surface-glass/10 backdrop-blur-sm border border-surface-glass/10 flex items-center justify-center hover:bg-accent hover:border-accent transition-all duration-300"
                  variants={scaleIn}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.15, rotate: 5 }}
                >
                  <Icon size={18} className="text-primary-foreground/70 group-hover:text-accent-foreground transition-colors" />
                </m.a>
              ))}
            </div>

            {/* Subsidiary */}
            <div className="pt-2">
              <span className="text-xs text-primary-foreground/50 uppercase tracking-widest">
                {t('footer.subsidiary')}
              </span>
              <a href="https://www.mtbglobalgroup.com" target="_blank" rel="noopener noreferrer" className="mt-2 bg-surface-glass/5 backdrop-blur-sm rounded-lg p-2.5 inline-block border border-surface-glass/5 hover:bg-surface-glass/10 transition-colors">
                <img src={logoGlobal} alt="MTB Global Group" className="h-8 w-auto opacity-80" loading="lazy" decoding="async" />
              </a>
            </div>
          </m.div>

          {/* Navigation — 2 cols */}
          <m.div
            className="lg:col-span-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={1}
          >
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-accent mb-5">
              Navigation
            </h4>
            <m.ul className="space-y-2.5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {navLinks.map((link) => (
                <m.li key={link.path} variants={staggerItem}>
                  <Link
                    to={link.path}
                    className="group flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-accent transition-colors"
                  >
                    <span className="w-1 h-1 bg-accent/40 rounded-full group-hover:bg-accent group-hover:scale-150 transition-all" />
                    {link.label}
                  </Link>
                </m.li>
              ))}
            </m.ul>
          </m.div>

          {/* Informations — 2 cols */}
          <m.div
            className="lg:col-span-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={2}
          >
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-accent mb-5">
              Informations
            </h4>
            <m.ul className="space-y-2.5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {infoLinks.map((link) => (
                <m.li key={link.path} variants={staggerItem}>
                  <Link
                    to={link.path}
                    className="group flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-accent transition-colors"
                  >
                    <span className="w-1 h-1 bg-accent/40 rounded-full group-hover:bg-accent group-hover:scale-150 transition-all" />
                    {link.label}
                  </Link>
                </m.li>
              ))}
              <m.li variants={staggerItem}>
                <a
                  href={getProfileUrl('fr')}
                  download={getProfileDownloadName('fr')}
                  className="group flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-accent transition-colors"
                >
                  <span className="w-1 h-1 bg-accent/40 rounded-full group-hover:bg-accent group-hover:scale-150 transition-all" />
                  {t('cta.profile.fr')}
                </a>
              </m.li>
              <m.li variants={staggerItem}>
                <a
                  href={getProfileUrl('en')}
                  download={getProfileDownloadName('en')}
                  className="group flex items-center gap-2 text-sm text-primary-foreground/70 hover:text-accent transition-colors"
                >
                  <span className="w-1 h-1 bg-accent/40 rounded-full group-hover:bg-accent group-hover:scale-150 transition-all" />
                  {t('cta.profile.en')}
                </a>
              </m.li>
            </m.ul>
          </m.div>

          {/* Contact — 4 cols */}
          <m.div
            className="lg:col-span-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            custom={3}
          >
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-accent mb-5">
              {t('nav.contact')}
            </h4>
            <div className="space-y-3">
              {contactItems.map((item, i) => {
                const inner = (
                  <>
                    <div className="w-9 h-9 bg-accent/15 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-accent/25 transition-colors">
                      <item.icon size={16} className="text-accent" />
                    </div>
                    <div className={item.type === 'div' && i === 0 ? "text-xs text-primary-foreground/70 leading-relaxed" : "text-sm"}>
                      <p className={`font-medium text-primary-foreground ${i === 0 ? 'text-sm' : ''} mb-0.5`}>{item.title}</p>
                      <span className="text-primary-foreground/70 text-xs">{item.content}</span>
                    </div>
                  </>
                );

                const cls = "flex items-start gap-3 p-3 rounded-xl bg-surface-glass/5 backdrop-blur-sm border border-surface-glass/5 hover:border-accent/20 transition-colors group";

                return (
                  <m.div key={i} variants={scaleIn} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                    {item.type === 'a' ? (
                      <a
                        href={item.href}
                        target={item.target}
                        rel={item.target ? 'noreferrer' : undefined}
                        className={cls}
                        onClick={() => {
                          if ('cta' in item && item.cta) {
                            trackCtaClick(item.cta, 'footer', { path: pathname, language });
                          }
                        }}
                      >
                        {inner}
                      </a>
                    ) : (
                      <div className={cls}>{inner}</div>
                    )}
                  </m.div>
                );
              })}
            </div>
          </m.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <m.div
        className="relative border-t border-primary-foreground/10 bg-black/20 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-primary-foreground/60">
              <Building2 size={14} className="text-accent/70" />
              <span>{t('footer.copyright')}</span>
            </div>
            <p className="text-xs text-primary-foreground/50">
              {t('footer.rights')}
            </p>
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-xs text-primary-foreground/60 underline underline-offset-4 hover:text-accent transition-colors"
            >
              {t('privacy.cookies.manage')}
            </button>

          </div>
        </div>
      </m.div>
    </footer>
  );
};

export default Footer;
